import React, { useEffect } from "react";

/**
 * Imperatively manages the document <head> for a marketing route: title, meta
 * description, canonical, Open Graph, Twitter card and JSON-LD blocks.
 *
 * Why not react-helmet? This app is CRA (no head manager). These tags are set
 * in an effect so they exist in the DOM by the time react-snap snapshots the
 * page — that's what puts the copy/schema into the *served* HTML for crawlers
 * and AI assistants. Tags created here are marked `data-seohead` so each mount
 * clears the previous route's additions before writing its own.
 */

const SITE_ORIGIN = "https://myedulink.net";

export interface SeoHeadProps {
    title: string;
    description: string;
    /** Path only, e.g. "/vs/teachable" — origin is prepended for canonical/OG. */
    canonicalPath: string;
    /** Absolute or site-relative image URL for OG/Twitter cards. */
    image?: string;
    /** JSON-LD objects (e.g. FAQPage, Product) injected as <script type=ld+json>. */
    jsonLd?: Record<string, unknown>[];
}

const absolute = (path: string) =>
    path.startsWith("http") ? path : `${SITE_ORIGIN}${path.startsWith("/") ? "" : "/"}${path}`;

const upsertMeta = (selector: string, attr: "name" | "property", key: string, content: string) => {
    let el = document.head.querySelector<HTMLMetaElement>(selector);
    if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        el.setAttribute("data-seohead", "");
        document.head.appendChild(el);
    }
    el.setAttribute("content", content);
};

const upsertLink = (rel: string, href: string) => {
    let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
    if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        el.setAttribute("data-seohead", "");
        document.head.appendChild(el);
    }
    el.setAttribute("href", href);
};

const SeoHead: React.FC<SeoHeadProps> = ({ title, description, canonicalPath, image, jsonLd }) => {
    useEffect(() => {
        const canonical = absolute(canonicalPath);
        const img = absolute(image ?? "/assets/images/slider/slider-1.png");

        // Remove JSON-LD this component previously injected (leave index.html's schema alone).
        document.head.querySelectorAll('script[data-seohead-jsonld]').forEach(n => n.remove());

        document.title = title;
        upsertMeta('meta[name="description"]', "name", "description", description);
        upsertLink("canonical", canonical);

        upsertMeta('meta[property="og:type"]', "property", "og:type", "website");
        upsertMeta('meta[property="og:title"]', "property", "og:title", title);
        upsertMeta('meta[property="og:description"]', "property", "og:description", description);
        upsertMeta('meta[property="og:url"]', "property", "og:url", canonical);
        upsertMeta('meta[property="og:image"]', "property", "og:image", img);

        upsertMeta('meta[name="twitter:card"]', "name", "twitter:card", "summary_large_image");
        upsertMeta('meta[name="twitter:title"]', "name", "twitter:title", title);
        upsertMeta('meta[name="twitter:description"]', "name", "twitter:description", description);
        upsertMeta('meta[name="twitter:image"]', "name", "twitter:image", img);

        (jsonLd ?? []).forEach(obj => {
            const script = document.createElement("script");
            script.type = "application/ld+json";
            script.setAttribute("data-seohead-jsonld", "");
            script.textContent = JSON.stringify(obj);
            document.head.appendChild(script);
        });

        return () => {
            document.head.querySelectorAll('script[data-seohead-jsonld]').forEach(n => n.remove());
        };
    }, [title, description, canonicalPath, image, jsonLd]);

    return null;
};

export default SeoHead;
